import ChevronIcon from '@centered/components/dist/icons/ChevronIcon'
import PauseIcon from '@centered/components/dist/icons/PauseIcon'
import PlayIcon from '@centered/components/dist/icons/PlayIcon'
import { FIREBASE_CLOUD_FUNCTIONS_URL } from '@centered/firebase'
import { shuffleArray } from '@centered/helpers'
import { Group } from '@centered/types'
import { AnimateSharedLayout, motion } from 'framer-motion'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import Wrapper from '../shared/Wrapper'

type Context = 'landing page' | 'coach page'

const heading: Record<Context, string> = {
  'coach page': 'Join the Pantheon of Productivity Coaches',
  'landing page': 'Pick Your Perfect Coach',
}

const subheading: Record<Context, string> = {
  'coach page':
    'Some of the other creators who engage & bond with their audiences on Sukha',
  'landing page':
    'Find the AI-powered coach that inspires you to fly through your work & free up your time.',
}

const buttonText: Record<Context, string> = {
  'coach page': 'Hear a sample',
  'landing page': 'Meet Your Coach',
}

interface ChooseYourCoachProps {
  coaches: Pick<
    Group,
    'introAudioUrl' | 'name' | 'coach' | 'id' | 'featureUrl' | 'coverUrl'
  >[]
  context?: Context
}

export default function ChooseYourCoach({
  coaches: unshuffledCoaches = [],
  context = 'landing page',
}: ChooseYourCoachProps): JSX.Element {
  const [coaches, setCoaches] = useState(unshuffledCoaches)

  useEffect(() => {
    const shuffledCoaches = Array.from(unshuffledCoaches)
    shuffleArray(shuffledCoaches)

    setCoaches(unshuffledCoaches)
  }, [unshuffledCoaches])

  const [centralCardIdx, setCentralCardIdx] = useState(2)

  function getCardAtIdx(idx: number) {
    if (idx < 0) {
      const modifiedIdx = coaches.length + idx
      return coaches[modifiedIdx]
    }

    if (idx >= coaches.length) {
      return coaches[idx - coaches.length]
    }

    return coaches[idx]
  }

  const cards = [
    getCardAtIdx(centralCardIdx - 3),
    getCardAtIdx(centralCardIdx - 2),
    getCardAtIdx(centralCardIdx - 1),
    getCardAtIdx(centralCardIdx),
    getCardAtIdx(centralCardIdx + 1),
    getCardAtIdx(centralCardIdx + 2),
    getCardAtIdx(centralCardIdx + 3),
  ]

  const audioRef = useRef<HTMLAudioElement>(null)
  const [audioPlaying, setAudioPlaying] = useState(false)

  return (
    <div className="pb-20">
      <audio
        ref={audioRef}
        hidden
        onPlay={() => setAudioPlaying(true)}
        onEnded={() => setAudioPlaying(false)}
        onPause={() => setAudioPlaying(false)}
      ></audio>

      <Wrapper className="mb-20">
        <h1 className="text-center text-3xl font-semibold mb-4">
          {heading[context]}
        </h1>
        <p className="text-white text-opacity-40 text-center">
          {subheading[context]}
        </p>
      </Wrapper>

      <Wrapper>
        <div className="flex w-full justify-center items-center ">
          <div
            onClick={() => {
              let newIdx = centralCardIdx - 1
              if (newIdx < 0) {
                newIdx = coaches.length - 1
              }
              setCentralCardIdx(newIdx)
            }}
            className="h-full text-white text-opacity-80 text-3xl cursor-pointer"
          >
            <ChevronIcon direction="left" />
          </div>

          <AnimateSharedLayout>
            {cards.map((card, i) => {
              if (!card) {
                return <></>
              }

              return (
                <motion.div layout key={card.id}>
                  <div
                    onClick={() => {
                      setCentralCardIdx(
                        coaches.findIndex((coach) => coach.id === card.id),
                      )

                      if (audioRef.current) {
                        audioRef.current.src =
                          card.introAudioUrl ??
                          'https://d10gj7r7js3e4x.cloudfront.net/Intro_009.mp3'
                        audioRef.current.play()
                      }
                    }}
                    className={`cursor-pointer relative transition-all hover:opacity-80 ${
                      i === 0 || i === cards.length - 1 ? 'hidden' : ''
                    } ${
                      i === 1 || i === cards.length - 2 ? 'hidden md:block' : ''
                    }`}
                    style={{
                      transform: `scale(${getScaleFromIdx(i)})`,
                      transformOrigin: getTransformOriginFromIdx(i),
                      width: 220,
                      height: 319,
                      overflow: 'hidden',
                    }}
                  >
                    <Image
                      src={
                        card.featureUrl ??
                        card.coverUrl ??
                        `${FIREBASE_CLOUD_FUNCTIONS_URL}/randomPhoto?collectionId=j1fwrKAGDIg`
                      }
                      alt={card.name}
                      fill
                      className="rounded-lg object-cover"
                    />
                  </div>
                </motion.div>
              )
            })}
          </AnimateSharedLayout>

          <div
            onClick={() => {
              let newIdx = centralCardIdx + 1
              if (newIdx >= coaches.length) {
                newIdx = 0
              }
              setCentralCardIdx(newIdx)
            }}
            className="h-full text-white text-opacity-80 text-3xl cursor-pointer"
          >
            <ChevronIcon direction="right" />
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <button
            onClick={() => {
              if (audioRef.current) {
                if (audioPlaying) {
                  audioRef.current.pause()
                  return
                }

                const card = cards[(cards.length - 1) / 2]

                audioRef.current.src =
                  card.introAudioUrl ??
                  'https://d10gj7r7js3e4x.cloudfront.net/Intro_009.mp3'
                audioRef.current.play()
              }
            }}
            className="px-4 py-2 text-sm rounded-full bg-gradient-to-r from-v5-pink to-v5-orange hover:opacity-80 transition-all flex items-center gap-2"
          >
            {audioPlaying ? <PauseIcon /> : <PlayIcon solid />}
            {buttonText[context]}
          </button>
        </div>
      </Wrapper>
    </div>
  )
}

function getScaleFromIdx(i: number) {
  if (i === 3) return 1
  if (i === 2 || i === 4) return 0.8
  if (i === 1 || i === 5) return 0.6
  return 0.8
}

function getTransformOriginFromIdx(i: number) {
  if (i === 1) return 'right'
  if (i === 7 - 2) return 'left'
  return 'center'
}
